/**
 * Demo Context for Testing Different User Types
 * 
 * This context provides easy switching between demo users and
 * populates the app with realistic test data for each user type.
 * 
 * ⚠️ IMPORTANT: Demo users are ONLY available in development!
 */

import React, { createContext, useContext, useState, useEffect } from 'react';
import { User, Guest } from '../types/user';
import { demoData, demoLoginCredentials, demoGuestUser } from '../data/demoUsers';
import { useAuth } from './SupabaseAuthContext';
import { isDemoModeAllowed, loggingConfig } from '../lib/config';

interface DemoContextType {
  // Current demo state
  currentDemoUser: User | Guest | null;
  isDemo: boolean;
  
  // Demo availability (environment-based)
  isDemoAllowed: boolean;
  
  // Demo user switching
  switchToDemoUser: (userType: string, userId?: string) => void;
  switchToDemoChild: (childId?: string) => void;
  switchToDemoParent: (parentId?: string) => void;
  switchToDemoTeacher: (teacherId?: string) => void;
  switchToDemoCreator: (creatorId?: string) => void;
  switchToDemoStudent: (studentId?: string) => void;
  switchToDemoGuest: () => void;
  
  // Demo data access
  getDemoProjects: (userId?: string) => unknown[];
  getDemoModels: (userId?: string) => unknown[];
  getDemoClassrooms: (teacherId?: string) => unknown[];
  getDemoStudents: (teacherId?: string) => unknown[];
  getDemoChildren: (parentId?: string) => unknown[];
  
  // Demo mode controls
  enableDemoMode: () => void;
  disableDemoMode: () => void;
  
  // Demo credentials
  getDemoCredentials: (userType: string) => unknown;
}

const DemoContext = createContext<DemoContextType | undefined>(undefined);

export const DemoProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [currentDemoUser, setCurrentDemoUser] = useState<User | Guest | null>(null);
  // Initialize demo mode to false by default
  const isDemoAllowed = isDemoModeAllowed();
  const [isDemo, setIsDemo] = useState<boolean>(false);
  const { setDemoUser } = useAuth();

  // Note: Demo mode is now allowed in all environments for demonstration purposes
  // This effect is kept for future use when we want to restrict demo mode again
  useEffect(() => {
    if (!isDemoAllowed && isDemo) {
      if (loggingConfig.logDemoActions) {
        console.warn('🚨 Demo mode disabled - not available in this environment');
      }
      setIsDemo(false);
      setCurrentDemoUser(null);
    }
  }, [isDemoAllowed, isDemo]);

  // Initialize with the 'Child' user when demo mode is enabled
  useEffect(() => {
    if (isDemoAllowed && isDemo && !currentDemoUser) {
      // Use the child demo user as default
      const childUser = demoData.users.find(u => u.type === 'child');
      if (childUser) {
        if (loggingConfig.logDemoActions) {
          console.log(`🎭 Auto-initialized demo user: ${childUser.name} (${childUser.type})`);
        }
        setCurrentDemoUser(childUser);
        setDemoUser(childUser);
        
        // Store demo user in localStorage for service wrapper detection
        localStorage.setItem('demo_user', JSON.stringify({
          id: childUser.id,
          type: childUser.type,
          name: childUser.name,
          timestamp: Date.now()
        }));
      }
    }
  }, [isDemo]); // Run when demo mode changes

  // Switch to a specific demo user type
  const switchToDemoUser = (userType: string, userId?: string) => {
    if (!isDemoAllowed) {
      console.warn('🚨 Demo users not available in production environment');
      return;
    }
    
    if (!isDemo) return;
    
    let user;
    
    if (userId) {
      // Find specific user by ID
      user = demoData.users.find(u => u.id === userId);
    } else {
      // Get first user of specified type
      user = demoData.users.find(u => u.type === userType);
    }
    
    if (user) {
      if (loggingConfig.logDemoActions) {
        console.log(`🎭 Switched to demo user: ${user.name} (${user.type})`);
      }
      setCurrentDemoUser(user);
      setDemoUser(user);
      
      // Store demo user in localStorage for service wrapper detection
      localStorage.setItem('demo_user', JSON.stringify({
        id: user.id,
        type: user.type,
        name: user.name,
        timestamp: Date.now()
      }));
    } else {
      console.warn(`No demo user found for type: ${userType}`);
    }
  };

  // Specific user type switching functions
  const switchToDemoChild = (childId?: string) => {
    if (!isDemoAllowed) return;
    const targetId = childId || demoData.users.find(u => u.type === 'child')?.id;
    if (targetId) switchToDemoUser('child', targetId);
  };
  
  const switchToDemoParent = (parentId?: string) => {
    if (!isDemoAllowed) return;
    const targetId = parentId || demoData.users.find(u => u.type === 'parent')?.id;
    if (targetId) switchToDemoUser('parent', targetId);
  };
  
  const switchToDemoTeacher = (teacherId?: string) => {
    if (!isDemoAllowed) return;
    const targetId = teacherId || demoData.users.find(u => u.type === 'teacher')?.id;
    if (targetId) switchToDemoUser('teacher', targetId);
  };
  
  const switchToDemoCreator = (creatorId?: string) => {
    if (!isDemoAllowed) return;
    const targetId = creatorId || demoData.users.find(u => u.type === 'creator')?.id;
    if (targetId) switchToDemoUser('creator', targetId);
  };
  
  const switchToDemoStudent = (studentId?: string) => {
    if (!isDemoAllowed) return;
    const targetId = studentId || demoData.users.find(u => u.type === 'student')?.id;
    if (targetId) switchToDemoUser('student', targetId);
  };
  
  const switchToDemoGuest = () => {
    if (!isDemoAllowed) {
      console.warn('🚨 Demo guest not available in production environment');
      return;
    }
    
    setCurrentDemoUser(demoGuestUser);
    setDemoUser(demoGuestUser);
    if (loggingConfig.logDemoActions) {
      console.log('🎭 Switched to demo guest user');
    }
  };

  // Get demo data for current user (only if demo is allowed)
  const getDemoProjects = (userId?: string) => {
    if (!isDemoAllowed) return [];
    
    const targetId = userId || currentDemoUser?.id;
    if (!targetId) return [];
    
    return demoData.sampleProjects.filter(project => project.ownerId === targetId);
  };
  
  const getDemoModels = (userId?: string) => {
    if (!isDemoAllowed) return [];
    
    const targetId = userId || currentDemoUser?.id;
    if (!targetId) return [];
    
    return demoData.sampleModels.filter(model => model.modelData.authorId === targetId);
  };
  
  const getDemoClassrooms = (teacherId?: string) => {
    if (!isDemoAllowed) return [];
    
    const targetId = teacherId || (currentDemoUser?.type === 'teacher' ? currentDemoUser.id : null);
    if (!targetId) return [];
    
    return demoData.relationships.classrooms.filter(classroom => classroom.teacherId === targetId);
  };
  
  const getDemoStudents = (teacherId?: string) => {
    if (!isDemoAllowed) return [];
    
    const targetId = teacherId || (currentDemoUser?.type === 'teacher' ? currentDemoUser.id : null);
    if (!targetId) return [];
    
    const studentIds = demoData.relationships.teacherStudentMap[targetId] || [];
    return studentIds.map(id => demoData.users.find(user => user.id === id)).filter(Boolean);
  };
  
  const getDemoChildren = (parentId?: string) => {
    if (!isDemoAllowed) return [];
    
    const targetId = parentId || (currentDemoUser?.type === 'parent' ? currentDemoUser.id : null);
    if (!targetId) return [];
    
    const childIds = demoData.relationships.parentChildMap[targetId] || [];
    return childIds.map(id => demoData.users.find(user => user.id === id)).filter(Boolean);
  };

  // Demo mode controls
  const enableDemoMode = () => {
    if (!isDemoAllowed) {
      console.warn('🚨 Demo mode not available in production environment');
      return;
    }
    setIsDemo(true);
    if (loggingConfig.logDemoActions) {
      console.log('🎭 Demo mode enabled');
    }
  };
  
  const disableDemoMode = () => {
    setIsDemo(false);
    setCurrentDemoUser(null);
    
    // Clear demo user from localStorage
    localStorage.removeItem('demo_user');
    
    if (loggingConfig.logDemoActions) {
      console.log('🎭 Demo mode disabled');
    }
  };

  // Get demo credentials for login (only if allowed)
  const getDemoCredentials = (userType: string) => {
    if (!isDemoAllowed) {
      console.warn('🚨 Demo credentials not available in production environment');
      return null;
    }
    
    switch (userType) {
      case 'child':
        return demoLoginCredentials.children[0];
      case 'student':
        return demoLoginCredentials.students[0];
      case 'parent':
        return demoLoginCredentials.parents[0];
      case 'teacher':
        return demoLoginCredentials.teachers[0];
      case 'creator':
        return demoLoginCredentials.creators[0];
      default:
        return null;
    }
  };

  return (
    <DemoContext.Provider
      value={{
        currentDemoUser,
        isDemo: isDemo && isDemoAllowed, // Only true if both demo is enabled AND allowed
        isDemoAllowed,
        switchToDemoUser,
        switchToDemoChild,
        switchToDemoParent,
        switchToDemoTeacher,
        switchToDemoCreator,
        switchToDemoStudent,
        switchToDemoGuest,
        getDemoProjects,
        getDemoModels,
        getDemoClassrooms,
        getDemoStudents,
        getDemoChildren,
        enableDemoMode,
        disableDemoMode,
        getDemoCredentials
      }}
    >
      {children}
    </DemoContext.Provider>
  );
};

export function useDemo() {
  const context = useContext(DemoContext);
  if (context === undefined) {
    throw new Error('useDemo must be used within a DemoProvider');
  }
  return context;
}

